import { useEffect, useMemo, useState } from 'react';
import { ISelectDiscountItem } from '~/components/ui/SelectDiscount';
import { IShopPreviewProps, VariationMode } from '~/components/ui/ShopPreview';
import { rage, useObservable } from '~/rage';
import { Gender, WalletType } from '~/shared';
import { groupItems } from '../components/ListTab';
import { StageTypes, useLayoutSelector } from '../redux';
import { useBuy } from './useBuy';

const useShopPreview = (
	activeVariants: Record<string, number>,
	onVariantChange: (itemId: string, newIndex: number) => void,
): IShopPreviewProps => {
	const activeStage = useLayoutSelector(
		(state) => state.clothesShop.activeStage,
	);
	const activeList = useLayoutSelector((state) => state.clothesShop.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.clothesShop.activeListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.clothesShop.activeCategory,
	);
	const shopItems = useLayoutSelector((state) => state.clothesShop.shopItems);
	const activeItemId = useLayoutSelector(
		(state) => state.clothesShop.activeItemId,
	);
	const searchValue = useLayoutSelector(
		(state) => state.clothesShop.searchValue,
	);
	const tabIndex = useLayoutSelector((state) => state.clothesShop.tabIndex);
	const ownedItems = useLayoutSelector((state) => state.clothesShop.ownedItems);
	const playerGender = useObservable(rage.player.getPlayerGender());
	const { handleBuy } = useBuy(activeVariants);
	const [discount, setDiscount] = useState<ISelectDiscountItem | null>(null);

	const items = useMemo(() => {
		const hasSearchValue = searchValue.trim().length > 0;

		if (tabIndex === 0) {
			if (hasSearchValue) {
				return groupItems(
					shopItems
						.filter(
							(item) =>
								item.name.toLowerCase().includes(searchValue.toLowerCase()) &&
								(item.gender === playerGender || item.gender === Gender.COMMON),
						)
						.map((item) => ({
							...item,
							active: false,
							new: false,
						})),
				);
			}
			return groupItems(
				shopItems
					.filter(
						(item) =>
							item.category === activeCategory &&
							(item.gender === playerGender || item.gender === Gender.COMMON),
					)
					.map((item) => ({
						...item,
						active: false,
						new: false,
					})),
			);
		}

		return groupItems(
			ownedItems
				.filter(
					(item) =>
						item.category === activeCategory &&
						(item.gender === playerGender || item.gender === Gender.COMMON),
				)
				.map((item) => ({
					...item,
					active: false,
					new: false,
				})),
		);
	}, [tabIndex, shopItems, ownedItems, activeCategory, searchValue]);

	const visible = useMemo(
		() => activeStage === StageTypes.List && activeListIndex !== null,
		[activeStage, activeListIndex],
	);

	const item = useMemo(() => {
		const foundByActiveId = items.find((group) =>
			group.items.some((item) => item.id === activeItemId),
		);
		if (foundByActiveId) {
			return foundByActiveId;
		}

		return activeList[activeListIndex || 0];
	}, [items, activeItemId, activeList, activeListIndex]);

	const itemIndex = useMemo(() => {
		if (!item?.id) return;
		return items.findIndex((i) => i.id === item.id);
	}, [items, item]);

	const variationItem = useMemo(() => {
		if (!item?.id) return;

		const variantIndex = activeVariants[item.id] || 0;
		return items.find((i) => i.id === item.id)?.items[variantIndex];
	}, [items, activeVariants, itemIndex, item]);

	const price = useMemo(() => {
		if (!variationItem?.price) return 0;
		if (!discount) return variationItem.price;
		return variationItem.price * (1 - discount.discount / 100);
	}, [variationItem, discount]);

	const selectDiscount = useMemo(() => {
		if (variationItem?.discount) return undefined;
		return {
			items: [
				null,
				{ id: 1, discount: 10 },
				{ id: 2, discount: 20 },
				{ id: 3, discount: 30 },
			],
			value: discount,
			onChange: setDiscount,
		};
	}, [variationItem, discount]);

	const oldPrice = useMemo(() => {
		if (!variationItem) return;

		if (variationItem.oldPrice) return variationItem.oldPrice;

		if (discount && discount.discount > 0) return variationItem.price;
	}, [variationItem, discount]);

	useEffect(() => {
		setDiscount(null);
	}, [item]);

	useEffect(() => {
		setDiscount(null);
	}, [item]);

	return {
		visible: visible && !!item?.id,
		name: variationItem?.name || '',
		price,
		oldPrice: oldPrice,
		handlePayByCash: () => handleBuy(WalletType.Cash),
		handlePayByCard: () => handleBuy(WalletType.Bank),
		handlePayByDonate: () => handleBuy(),
		walletType:
			variationItem?.wallet === WalletType.Donate
				? WalletType.Donate
				: WalletType.Cash,
		variationIndex: activeVariants[item?.id] || 0,
		onVariationChange: (newIndex) => onVariantChange(item?.id, newIndex),
		maxVariantIndex: items.find((i) => i.id === item?.id)?.items?.length || 0,
		variationMode: VariationMode.Variant,
		variationTitle: 'Расцветка',
		bottomTitle:
			'Одежда будет добавлена к вам в инвентарь, либо в инвентарь вашего дома',
		discount: variationItem?.discount ?? 0,
		selectDiscount: selectDiscount,
	};
};

export { useShopPreview };
